import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Building2,
  FileText,
  CreditCard,
  Mail,
  Trash2,
  Send,
  Bell,
  CheckCircle2,
  Clock,
  XCircle,
  Crown,
  Loader2,
  RefreshCw,
  Eye,
  AlertCircle,
  MoreVertical,
  Pause,
  Play,
  Key,
  UserX,
  UserCheck,
  Shield,
  Gift,
  UserPlus,
  CalendarPlus,
  ExternalLink,
  RefreshCcw,
  Copy,
  Download,
} from "lucide-react";
import { toast } from "sonner";

// Password validation and generation utilities
const generateStrongPassword = () => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%&*';
  
  let password = '';
  // Ensure at least one of each required type
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill remaining with mix of all
  const allChars = lowercase + uppercase + numbers + special;
  for (let i = 0; i < 4; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const validatePassword = (password) => {
  const errors = [];
  if (password.length < 6) {
    errors.push("At least 6 characters");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("At least one capital letter");
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("At least one special character (!@#$%&*)");
  }
  return errors;
};

export default function AdminDashboardPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [notifications, setNotifications] = useState({ recent_payments: [], recent_users: [] });
  
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // User management state
  const [actionLoading, setActionLoading] = useState(null); // Track which user action is loading
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState("");
  
  // Create User state
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    email: "",
    name: "",
    password: "",
    company_name: "",
    access_duration_days: "365"
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Extend Subscription state
  const [extendDialog, setExtendDialog] = useState({ open: false, user: null });
  const [extendData, setExtendData] = useState({ duration_days: "30", plan: "premium" });
  const [extending, setExtending] = useState(false);
  
  // User Details/Budgets state
  const [userDetailsDialog, setUserDetailsDialog] = useState({ open: false, user: null, data: null });
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAllData();
  }, []); // Intentionally run only on mount

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, propertiesRes, budgetsRes, paymentsRes, invitationsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/properties`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/budgets`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/payments`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/invitations`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/notifications`, { headers: getAuthHeader() }),
      ]);
      
      setUsers(usersRes.data.users);
      setProperties(propertiesRes.data.properties);
      setBudgets(budgetsRes.data.budgets);
      setPayments(paymentsRes.data.payments);
      setInvitations(invitationsRes.data.invitations);
      setNotifications(notificationsRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
      // Error handled
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${API}/admin/invitations`,
        { email: inviteEmail, name: inviteName || null },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteName("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail} and all their data?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success("User deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!confirm(`Delete property "${propertyName}" and all its budgets?`)) return;
    
    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`, { headers: getAuthHeader() });
      toast.success("Property deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!confirm("Delete this budget?")) return;
    
    try {
      await axios.delete(`${API}/admin/budgets/${budgetId}`, { headers: getAuthHeader() });
      toast.success("Budget deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete budget");
    }
  };

  const handleDownloadBudget = async (budgetId, propertyName, fiscalYear) => {
    try {
      toast.info("Preparing Excel download...");
      const response = await axios.get(
        `${API}/budgets/${budgetId}/export-excel`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `${propertyName || 'Budget'}_FY${fiscalYear || 'Unknown'}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${fileName}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.response?.data?.detail || "Failed to download budget");
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/admin/invitations/${invitationId}`, { headers: getAuthHeader() });
      toast.success("Invitation deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invitation");
    }
  };

  // User Management Handlers
  const handlePauseMembership = async (userId, userEmail) => {
    if (!confirm(`Pause membership for ${userEmail}?`)) return;
    
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/pause-membership`, {}, { headers: getAuthHeader() });
      toast.success(`Membership paused for ${userEmail}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pause membership");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeMembership = async (userId, userEmail) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/resume-membership`, {}, { headers: getAuthHeader() });
      toast.success(`Membership resumed for ${userEmail}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume membership");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    const { user } = resetPasswordDialog;
    setActionLoading(user.id);
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success(`Password reset for ${user.email}`);
      setResetPasswordDialog({ open: false, user: null });
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivateUser = async (userId, userEmail) => {
    if (!confirm(`Deactivate account for ${userEmail}? User will not be able to login.`)) return;
    
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/deactivate`, {}, { headers: getAuthHeader() });
      toast.success(`Account deactivated for ${userEmail}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate account");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivateUser = async (userId, userEmail) => {
    if (!confirm(`Reactivate account for ${userEmail}? They will get 30 days of access.`)) return;
    
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/reactivate`, {}, { headers: getAuthHeader() });
      toast.success(`Account reactivated for ${userEmail}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate account");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantTestAccess = async (userId, userEmail) => {
    if (!confirm(`Grant 90 days test access to ${userEmail}? No payment required.`)) return;
    
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/grant-test-access`, {}, { headers: getAuthHeader() });
      toast.success(`Test access granted to ${userEmail} for 90 days`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant test access");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserData.email || !createUserData.name || !createUserData.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate password requirements
    const passwordErrors = validatePassword(createUserData.password);
    if (passwordErrors.length > 0) {
      toast.error(`Password requirements not met: ${passwordErrors.join(", ")}`);
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/create`,
        {
          email: createUserData.email,
          name: createUserData.name,
          password: createUserData.password,
          company_name: createUserData.company_name || null,
          access_duration_days: parseInt(createUserData.access_duration_days)
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`User created! Login details sent to ${createUserData.email} and copied to your email.`);
      setCreateUserDialog(false);
      setCreateUserData({ email: "", name: "", password: "", company_name: "", access_duration_days: "365" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword();
    setCreateUserData({ ...createUserData, password: newPassword });
    toast.success("Strong password generated!");
  };

  const handleCopyPassword = () => {
    if (createUserData.password) {
      navigator.clipboard.writeText(createUserData.password);
      toast.success("Password copied to clipboard!");
    }
  };

  const handleExtendSubscription = async () => {
    const { user } = extendDialog;
    setExtending(true);
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/extend-subscription`,
        {
          duration_days: parseInt(extendData.duration_days),
          plan: extendData.plan
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Subscription extended for ${user.email} - ${extendData.duration_days} days of ${extendData.plan}`);
      setExtendDialog({ open: false, user: null });
      setExtendData({ duration_days: "30", plan: "premium" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend subscription");
    } finally {
      setExtending(false);
    }
  };

  const handleViewUserDetails = async (user) => {
    setLoadingUserDetails(true);
    setUserDetailsDialog({ open: true, user, data: null });
    try {
      const response = await axios.get(
        `${API}/admin/users/${user.id}/details`,
        { headers: getAuthHeader() }
      );
      setUserDetailsDialog({ open: true, user, data: response.data });
    } catch (error) {
      toast.error("Failed to load user details");
      setUserDetailsDialog({ open: false, user: null, data: null });
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const getStatusBadge = (subscription) => {
    if (!subscription) return <Badge variant="outline" className="text-slate-500">No Plan</Badge>;
    if (subscription.status === "active") {
      if (subscription.plan === "trial") return <Badge className="bg-[#E8EDE5] text-[#3C4C33]">Trial</Badge>;
      if (subscription.plan === "test") return <Badge className="bg-[#F5EDCE] text-[#8B7355]">Test Access</Badge>;
      if (subscription.plan === "premium") return <Badge className="bg-emerald-100 text-emerald-700">Premium</Badge>;
      return <Badge className="bg-blue-100 text-blue-700">Basic</Badge>;
    }
    return <Badge variant="outline" className="text-red-500">Expired</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage users, properties, and view payments</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="gap-2 w-full sm:w-auto">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                <p className="text-sm text-slate-500">Users</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{properties.length}</p>
                <p className="text-sm text-slate-500">Properties</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#E8EDE5] rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#4B5E40]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{budgets.length}</p>
                <p className="text-sm text-slate-500">Budgets</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{payments.filter(p => p.payment_status === "paid").length}</p>
                <p className="text-sm text-slate-500">Payments</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{invitations.filter(i => i.status === "pending").length}</p>
                <p className="text-sm text-slate-500">Pending Invites</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications Banner */}
        {notifications.recent_payments?.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-800">
                  {notifications.recent_payments.length} new payment(s) in the last 30 days
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="bg-slate-100 flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="users" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" /> Users
            </TabsTrigger>
            <TabsTrigger value="properties" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Building2 className="w-3 h-3 sm:w-4 sm:h-4" /> Properties
            </TabsTrigger>
            <TabsTrigger value="budgets" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" /> Budgets
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" /> Payments
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4" /> Invites
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>Manage registered users, subscriptions, and account status</CardDescription>
                </div>
                <Button 
                  onClick={() => setCreateUserDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Create User
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <ScrollArea className="h-[500px]">
                    <table className="w-full min-w-[800px]">
                      <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Account</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Subscription</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Properties</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Joined</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className={`border-b border-slate-100 hover:bg-slate-50 ${user.is_active === false ? 'bg-red-50/50' : ''}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_active === false ? 'bg-red-100' : 'bg-slate-200'}`}>
                                {user.is_admin ? (
                                  <Crown className="w-5 h-5 text-amber-500" />
                                ) : user.is_active === false ? (
                                  <UserX className="w-5 h-5 text-red-500" />
                                ) : (
                                  <Users className="w-5 h-5 text-slate-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{user.name}</p>
                                <p className="text-sm text-slate-500">{user.email}</p>
                                {user.company_name && (
                                  <p className="text-xs text-slate-400">{user.company_name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            {user.is_admin ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Shield className="w-3 h-3 mr-1" /> Admin
                              </Badge>
                            ) : user.is_active === false ? (
                              <Badge variant="outline" className="text-red-600 border-red-300">
                                <UserX className="w-3 h-3 mr-1" /> Deactivated
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <UserCheck className="w-3 h-3 mr-1" /> Active
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {user.is_admin ? (
                              <span className="text-slate-400 text-sm">Full Access</span>
                            ) : user.subscription?.status === "paused" ? (
                              <Badge className="bg-orange-100 text-orange-700">
                                <Pause className="w-3 h-3 mr-1" /> Paused
                              </Badge>
                            ) : (
                              getStatusBadge(user.subscription)
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-slate-900">{user.property_count} props</span>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="text-emerald-600 p-0 h-auto ml-1"
                              onClick={() => handleViewUserDetails(user)}
                            >
                              ({user.budget_count} budgets)
                            </Button>
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            {!user.is_admin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" disabled={actionLoading === user.id}>
                                    {actionLoading === user.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <MoreVertical className="w-4 h-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                  {/* View Details */}
                                  <DropdownMenuItem onClick={() => handleViewUserDetails(user)}>
                                    <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                    View Details & Budgets
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Extend/Change Subscription */}
                                  <DropdownMenuItem onClick={() => setExtendDialog({ open: true, user })}>
                                    <CalendarPlus className="w-4 h-4 mr-2 text-[#5A7A5A]" />
                                    Extend/Change Subscription
                                  </DropdownMenuItem>
                                  
                                  {/* Membership Actions */}
                                  {user.subscription && user.subscription.status === "active" && (
                                    <DropdownMenuItem onClick={() => handlePauseMembership(user.id, user.email)}>
                                      <Pause className="w-4 h-4 mr-2 text-orange-500" />
                                      Pause Membership
                                    </DropdownMenuItem>
                                  )}
                                  {user.subscription?.status === "paused" && (
                                    <DropdownMenuItem onClick={() => handleResumeMembership(user.id, user.email)}>
                                      <Play className="w-4 h-4 mr-2 text-emerald-500" />
                                      Resume Membership
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Grant Test Access - shows if user has no plan or expired */}
                                  {(!user.subscription || user.subscription.status !== "active") && user.is_active !== false && (
                                    <DropdownMenuItem onClick={() => handleGrantTestAccess(user.id, user.email)}>
                                      <Gift className="w-4 h-4 mr-2 text-[#C9A227]" />
                                      Grant Test Access (90 days)
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Password Reset */}
                                  <DropdownMenuItem onClick={() => setResetPasswordDialog({ open: true, user })}>
                                    <Key className="w-4 h-4 mr-2 text-blue-500" />
                                    Reset Password
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Account Status */}
                                  {user.is_active !== false ? (
                                    <DropdownMenuItem 
                                      onClick={() => handleDeactivateUser(user.id, user.email)}
                                      className="text-red-600"
                                    >
                                      <UserX className="w-4 h-4 mr-2" />
                                      Deactivate Account
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => handleReactivateUser(user.id, user.email)}
                                      className="text-emerald-600"
                                    >
                                      <UserCheck className="w-4 h-4 mr-2" />
                                      Reactivate Account
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Delete User */}
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteUser(user.id, user.email)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>All Properties</CardTitle>
                <CardDescription>View and manage all user properties</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                <ScrollArea className="h-[500px]">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Property</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Owner</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Rooms</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Budgets</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {properties.map((property) => (
                        <tr key={property.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{property.name}</p>
                                <p className="text-sm text-slate-500">{property.location}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <p className="text-slate-900">{property.owner?.name || "Unknown"}</p>
                            <p className="text-sm text-slate-500">{property.owner?.email}</p>
                          </td>
                          <td className="p-3 text-slate-900">{property.total_rooms}</td>
                          <td className="p-3 text-slate-900">{property.budget_count}</td>
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteProperty(property.id, property.name)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Budgets Tab */}
          <TabsContent value="budgets">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>All Budgets</CardTitle>
                <CardDescription>View and download all user budgets (data preserved even for terminated users)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                <ScrollArea className="h-[500px]">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Property</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Owner</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Fiscal Year</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Created</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgets.map((budget) => (
                        <tr key={budget.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3">
                            <p className="font-medium text-slate-900">{budget.property?.name || "Unknown"}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-slate-900">{budget.owner?.name || "Unknown"}</p>
                            <p className="text-sm text-slate-500">{budget.owner?.email}</p>
                            {budget.owner?.is_deleted && (
                              <Badge variant="outline" className="text-red-500 text-xs mt-1">Terminated User</Badge>
                            )}
                          </td>
                          <td className="p-3 text-slate-900">FY{budget.fiscal_year_start}</td>
                          <td className="p-3 text-sm text-slate-500">
                            {new Date(budget.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1"
                              onClick={() => handleDownloadBudget(budget.id, budget.property?.name, budget.fiscal_year_start)}
                              data-testid={`download-budget-${budget.id}`}
                              title="Download Excel"
                            >
                              <Download className="w-4 h-4" />
                              <span className="hidden sm:inline">Download</span>
                            </Button>
                            <Link to={`/reports/${budget.id}`}>
                              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" title="View in App">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBudget(budget.id)}
                              title="Delete Budget"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>All payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                <ScrollArea className="h-[500px]">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Plan</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Amount</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3">
                            <p className="font-medium text-slate-900">{payment.user?.name || "Unknown"}</p>
                            <p className="text-sm text-slate-500">{payment.user_email}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{payment.plan_id || "Unknown"}</Badge>
                          </td>
                          <td className="p-3 font-mono text-slate-900">${payment.amount}</td>
                          <td className="p-3">
                            {payment.payment_status === "paid" ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                              </Badge>
                            ) : payment.payment_status === "pending" ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" /> Pending
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-red-500">
                                <XCircle className="w-3 h-3 mr-1" /> Failed
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {new Date(payment.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {payments.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">
                            No payments yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invites">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Send Invitation Form */}
              <Card className="border-emerald-200 lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Send className="w-5 h-5 text-emerald-600" />
                    Send Invitation
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Invite users to try HotelBudget with 3 days free Premium access</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Name (Optional)</Label>
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                    />
                  </div>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleSendInvite}
                    disabled={sendingInvite}
                    data-testid="send-invite-btn"
                  >
                    {sendingInvite ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Invitation
                  </Button>
                </CardContent>
              </Card>

              {/* Invitations List */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle>All Invitations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                  <ScrollArea className="h-[400px]">
                    <table className="w-full min-w-[500px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Email</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Code</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Sent</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitations.map((invite) => (
                          <tr key={invite.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-3">
                              <p className="font-medium text-slate-900">{invite.email}</p>
                              {invite.name && <p className="text-sm text-slate-500">{invite.name}</p>}
                            </td>
                            <td className="p-3">
                              <code className="bg-slate-100 px-2 py-1 rounded text-sm">{invite.code}</code>
                            </td>
                            <td className="p-3">
                              {invite.status === "pending" ? (
                                <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                              ) : invite.status === "accepted" ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Accepted</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500">Expired</Badge>
                              )}
                            </td>
                            <td className="p-3 text-sm text-slate-500">
                              {new Date(invite.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              {invite.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteInvitation(invite.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {invitations.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-slate-500">
                              No invitations sent yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </ScrollArea>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => {
        setResetPasswordDialog({ open, user: open ? resetPasswordDialog.user : null });
        if (!open) setNewPassword("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordDialog.user?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={actionLoading === resetPasswordDialog.user?.id}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {actionLoading === resetPasswordDialog.user?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onOpenChange={(open) => {
        setCreateUserDialog(open);
        if (!open) {
          setCreateUserData({ email: "", name: "", password: "", company_name: "", access_duration_days: "365" });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Create New User
            </DialogTitle>
            <DialogDescription>
              Create a user with full premium access. They will be prompted to change their password on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="createEmail">Email Address *</Label>
              <Input
                id="createEmail"
                type="email"
                placeholder="user@example.com"
                value={createUserData.email}
                onChange={(e) => setCreateUserData({ ...createUserData, email: e.target.value })}
                data-testid="create-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="createName">Full Name *</Label>
              <Input
                id="createName"
                type="text"
                placeholder="John Smith"
                value={createUserData.name}
                onChange={(e) => setCreateUserData({ ...createUserData, name: e.target.value })}
                data-testid="create-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="createPassword">Password *</Label>
              <div className="flex gap-2">
                <Input
                  id="createPassword"
                  type="text"
                  placeholder="Min 6 chars, 1 capital, 1 special"
                  value={createUserData.password}
                  onChange={(e) => setCreateUserData({ ...createUserData, password: e.target.value })}
                  data-testid="create-user-password"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleGeneratePassword}
                  title="Generate strong password"
                >
                  <RefreshCcw className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyPassword}
                  title="Copy password"
                  disabled={!createUserData.password}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              {createUserData.password && (
                <div className="text-xs space-y-1">
                  {validatePassword(createUserData.password).length > 0 ? (
                    <div className="text-red-500">
                      Missing: {validatePassword(createUserData.password).join(", ")}
                    </div>
                  ) : (
                    <div className="text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Password meets requirements
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-500">User will be required to change this on first login. Login details will be emailed to user.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="createCompany">Company Name (Optional)</Label>
              <Input
                id="createCompany"
                type="text"
                placeholder="Hotel Company Ltd"
                value={createUserData.company_name}
                onChange={(e) => setCreateUserData({ ...createUserData, company_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Access Duration</Label>
              <Select 
                value={createUserData.access_duration_days} 
                onValueChange={(value) => setCreateUserData({ ...createUserData, access_duration_days: value })}
              >
                <SelectTrigger data-testid="create-user-duration">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                  <SelectItem value="730">2 Years</SelectItem>
                  <SelectItem value="1825">5 Years</SelectItem>
                  <SelectItem value="3650">10 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={creatingUser}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-user-submit"
            >
              {creatingUser ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Subscription Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => {
        setExtendDialog({ open, user: open ? extendDialog.user : null });
        if (!open) setExtendData({ duration_days: "30", plan: "premium" });
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-[#4B5E40]" />
              Extend/Change Subscription
            </DialogTitle>
            <DialogDescription>
              Modify subscription for {extendDialog.user?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subscription Plan</Label>
              <Select 
                value={extendData.plan} 
                onValueChange={(value) => setExtendData({ ...extendData, plan: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium (Full Access)</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="test">Test Access</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select 
                value={extendData.duration_days} 
                onValueChange={(value) => setExtendData({ ...extendData, duration_days: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 Days</SelectItem>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="60">60 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                  <SelectItem value="730">2 Years</SelectItem>
                  <SelectItem value="1825">5 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleExtendSubscription}
              disabled={extending}
              className="bg-[#4B5E40] hover:bg-[#3C4C33]"
            >
              {extending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              Update Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details & Budgets Dialog */}
      <Dialog open={userDetailsDialog.open} onOpenChange={(open) => {
        setUserDetailsDialog({ open, user: open ? userDetailsDialog.user : null, data: open ? userDetailsDialog.data : null });
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-slate-600" />
              User Details: {userDetailsDialog.user?.email}
            </DialogTitle>
          </DialogHeader>
          
          {loadingUserDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : userDetailsDialog.data ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Account Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Name:</span> <span className="font-medium">{userDetailsDialog.data.user?.name}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="font-medium">{userDetailsDialog.data.user?.email}</span></div>
                  <div><span className="text-slate-500">Company:</span> <span className="font-medium">{userDetailsDialog.data.user?.company_name || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Country:</span> <span className="font-medium">{userDetailsDialog.data.user?.country || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Created:</span> <span className="font-medium">{new Date(userDetailsDialog.data.user?.created_at).toLocaleDateString()}</span></div>
                  <div><span className="text-slate-500">Active:</span> <span className={`font-medium ${userDetailsDialog.data.user?.is_active !== false ? 'text-emerald-600' : 'text-red-600'}`}>{userDetailsDialog.data.user?.is_active !== false ? 'Yes' : 'No'}</span></div>
                </div>
              </div>

              {/* Password Info */}
              <div className="bg-amber-50 rounded-lg p-4">
                <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Password Information
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-amber-700">Has Password:</span> <span className="font-medium">{userDetailsDialog.data.user?.password_info?.has_password ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-amber-700">Account Type:</span> <span className="font-medium capitalize">{userDetailsDialog.data.user?.password_info?.account_type?.replace(/_/g, ' ') || 'Self Registered'}</span></div>
                  <div><span className="text-amber-700">Change Required:</span> <span className={`font-medium ${userDetailsDialog.data.user?.password_info?.password_change_required ? 'text-red-600' : 'text-emerald-600'}`}>{userDetailsDialog.data.user?.password_info?.password_change_required ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-amber-700">Last Changed:</span> <span className="font-medium">{userDetailsDialog.data.user?.password_info?.password_changed_at ? new Date(userDetailsDialog.data.user?.password_info?.password_changed_at).toLocaleString() : 'Never'}</span></div>
                  {userDetailsDialog.data.user?.password_info?.created_by_admin && (
                    <div className="col-span-2"><span className="text-amber-700">Created By Admin:</span> <span className="font-medium">{userDetailsDialog.data.user?.password_info?.created_by_admin}</span></div>
                  )}
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    setResetPasswordDialog({ open: true, user: userDetailsDialog.user });
                  }}
                >
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
              </div>

              {/* Subscription */}
              <div className="bg-[#F3F5F2] rounded-lg p-4">
                <h3 className="font-semibold text-[#2A3525] mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Subscription
                </h3>
                {userDetailsDialog.data.subscription ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#3C4C33]">Plan:</span> <span className="font-medium capitalize">{userDetailsDialog.data.subscription.plan}</span></div>
                    <div><span className="text-[#3C4C33]">Status:</span> <span className={`font-medium ${userDetailsDialog.data.subscription.status === 'active' ? 'text-emerald-600' : 'text-red-600'}`}>{userDetailsDialog.data.subscription.status}</span></div>
                    <div><span className="text-[#3C4C33]">Expires:</span> <span className="font-medium">{new Date(userDetailsDialog.data.subscription.expires_at).toLocaleDateString()}</span></div>
                    {userDetailsDialog.data.subscription.extended_by && (
                      <div><span className="text-[#3C4C33]">Extended By:</span> <span className="font-medium">{userDetailsDialog.data.subscription.extended_by}</span></div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500">No subscription</p>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    setExtendDialog({ open: true, user: userDetailsDialog.user });
                  }}
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Extend/Change Subscription
                </Button>
              </div>

              {/* Properties & Budgets */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Properties ({userDetailsDialog.data.properties_count}) & Budgets ({userDetailsDialog.data.budgets_count})
                </h3>
                
                {userDetailsDialog.data.properties?.length > 0 ? (
                  <div className="space-y-3">
                    {userDetailsDialog.data.properties.map((property) => (
                      <div key={property.id} className="bg-white rounded-lg p-3 border border-emerald-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-slate-900">{property.property_name}</h4>
                            <p className="text-sm text-slate-500">{property.location} · {property.num_rooms} rooms</p>
                          </div>
                        </div>
                        {/* Budgets for this property */}
                        <div className="mt-2">
                          {userDetailsDialog.data.budgets?.filter(b => b.property_id === property.id).length > 0 ? (
                            <div className="space-y-1">
                              {userDetailsDialog.data.budgets?.filter(b => b.property_id === property.id).map((budget) => (
                                <div key={budget.id} className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded">
                                  <span className="text-slate-700">{budget.budget_name || `Budget ${budget.year}`}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500">{budget.year}</span>
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="h-6 px-2 text-emerald-600"
                                      onClick={() => handleDownloadBudget(budget.id, property.property_name, budget.year)}
                                      title="Download Excel"
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Download
                                    </Button>
                                    <Link to={`/budgets/${budget.id}/configure`}>
                                      <Button size="sm" variant="ghost" className="h-6 px-2 text-blue-600">
                                        <ExternalLink className="w-3 h-3 mr-1" />
                                        Open
                                      </Button>
                                    </Link>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400">No budgets</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">No properties created</p>
                )}
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDetailsDialog({ open: false, user: null, data: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
